import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ContactFormField from "./ContactFormField";
import "./contact-form.css";
import { services } from "../../data/services";
import { appendContact } from "../../utils/adminInboxStore";
import { mirrorPublicContactMessageToLocal } from "../../config/publicApiFeature";
import { submitContactMessagePublic } from "../../controllers/publicContactMessageController";
import { createContactFormResolver } from "./contactFormResolver";
import { useAppDispatch } from "../../store/hooks";
import { pushToast } from "../../store/slices/toastSlice";

const emptyForm = {
  fullName: "",
  email: "",
  phone: "",
  country: "",
  company: "",
  message: "",
  newsletter: false,
};

const ContactForm = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const errorsSummaryRef = useRef(null);
  const [submitBanner, setSubmitBanner] = useState(false);
  const formUid = useId().replace(/:/g, "");
  const newsletterFieldId = useId().replace(/:/g, "");

  const resolver = useMemo(() => createContactFormResolver(t), [t]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: emptyForm,
    resolver,
    mode: "onSubmit",
  });

  const { isDirty } = useFormState({ control });

  const fullName = watch("fullName");
  const email = watch("email");
  const phone = watch("phone");
  const country = watch("country");
  const company = watch("company");
  const message = watch("message");
  const newsletter = watch("newsletter");

  useEffect(() => {
    if (isDirty) setSubmitBanner(false);
  }, [isDirty]);

  useEffect(() => {
    clearErrors("root");
  }, [fullName, email, phone, country, company, message, newsletter, clearErrors]);

  useEffect(() => {
    const raw = searchParams.get("svc");
    if (!raw) return;
    const svcId = decodeURIComponent(raw).trim();
    if (!services.some((s) => s.id === svcId)) return;
    const title = t(`serviceCatalog.${svcId}.title`);
    const line = t("contact.serviceInterestLine", { title });
    const cur = getValues("message");
    if (String(cur).includes(line)) return;
    const next =
      String(cur).trim() === "" ? line : `${String(cur).trim()}\n\n${line}`;
    setValue("message", next, { shouldDirty: true, shouldValidate: false });
  }, [searchParams, t, getValues, setValue]);

  const onValidSubmit = useCallback(
    async (data) => {
      clearErrors("root");
      const localPayload = {
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        country: data.country,
        company: data.company,
        message: data.message,
        newsletter: data.newsletter,
      };
      const apiBody = {
        email: data.email,
        name: data.fullName,
        phone: data.phone,
        country: data.country,
        company: data.company,
        message: data.message,
        body: data.message,
        subject: `Contact — ${data.company?.trim() || data.fullName}`,
        newsletter: data.newsletter,
        metadata: { locale: i18n.language || "en" },
      };

      const r = await submitContactMessagePublic(apiBody);
      if (!r.ok) {
        const msg = r.errorMessage || t("contact.submitServerError");
        setError("root", {
          type: "server",
          message: msg,
        });
        dispatch(
          pushToast({
            variant: "error",
            title: t("contact.toastErrorTitle"),
            message: msg,
          })
        );
        return;
      }

      if (mirrorPublicContactMessageToLocal()) {
        appendContact({
          ...localPayload,
          ...(typeof r.data?.id === "string" ? { id: r.data.id } : {}),
        });
      }

      reset(emptyForm);
      setSubmitBanner(true);
      dispatch(
        pushToast({
          variant: "success",
          title: t("contact.toastSuccessTitle"),
          message: t("contact.toastSuccessBody"),
        })
      );
    },
    [clearErrors, dispatch, i18n.language, reset, setError, t]
  );

  const topErrors = useMemo(() => {
    const collected = [];
    if (errors.root?.message) collected.push(errors.root.message);
    ["fullName", "email", "phone", "country", "company", "message"].forEach((key) => {
      const m = errors[key]?.message;
      if (m && typeof m === "string") collected.push(m);
    });
    return [...new Set(collected)];
  }, [errors]);
  const topErrorsKey = useMemo(() => topErrors.join("\u0001"), [topErrors]);

  useEffect(() => {
    if (topErrors.length === 0) return undefined;
    const run = () => {
      const el = errorsSummaryRef.current;
      if (!el) return;
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      el.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
      try {
        el.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
    };
    const id = requestAnimationFrame(() => requestAnimationFrame(run));
    return () => cancelAnimationFrame(id);
  }, [topErrorsKey, topErrors.length]);

  const fid = (name) => `vh-cf-${formUid}-${name}`;

  return (
    <form
      className="vh-cf"
      id={`vh-contact-form-${formUid}`}
      dir={i18n.dir()}
      noValidate
      aria-label={t("contact.formAria")}
      aria-busy={isSubmitting}
      onSubmit={handleSubmit(onValidSubmit)}
    >
      <div className="vh-cf-grid">
        {topErrors.length > 0 ? (
          <div
            ref={errorsSummaryRef}
            tabIndex={-1}
            className="vh-cf-alert"
            role="alert"
            aria-live="polite"
          >
            <ul className="vh-cf-alert__list">
              {topErrors.map((msg, i) => (
                <li key={`${i}-${msg}`} className="vh-cf-alert__item">
                  {msg}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <ContactFormField
          id={fid("fullName")}
          label={t("contact.fullName")}
          type="text"
          required
          maxLength={80}
          charCount={fullName?.length ?? 0}
          t={t}
          reg={register("fullName")}
          error={errors.fullName?.message}
        />

        <ContactFormField
          id={fid("email")}
          label={t("contact.email")}
          type="email"
          required
          reg={register("email")}
          error={errors.email?.message}
        />

        <ContactFormField
          id={fid("phone")}
          label={t("contact.phone")}
          type="tel"
          reg={register("phone")}
          error={errors.phone?.message}
        />

        <ContactFormField
          id={fid("country")}
          label={t("contact.country")}
          type="text"
          required
          maxLength={80}
          charCount={country?.length ?? 0}
          t={t}
          reg={register("country")}
          error={errors.country?.message}
        />

        <div className="vh-cf-field--span-2">
          <ContactFormField
            id={fid("company")}
            label={t("contact.company")}
            type="text"
            maxLength={80}
            charCount={company?.length ?? 0}
            t={t}
            reg={register("company")}
            error={errors.company?.message}
          />
        </div>

        <div className="vh-cf-field--span-2">
          <ContactFormField
            id={fid("message")}
            label={t("contact.message")}
            type="textarea"
            required
            textareaRows={6}
            maxLength={500}
            charCount={message?.length ?? 0}
            t={t}
            reg={register("message")}
            error={errors.message?.message}
          />
        </div>

        <label className="vh-cf-check" htmlFor={newsletterFieldId}>
          <input
            id={newsletterFieldId}
            type="checkbox"
            className="vh-cf-check__input"
            {...register("newsletter")}
          />
          <span className="vh-cf-check__text">{t("contact.newsletter")}</span>
        </label>
      </div>

      <p className="vh-cf-privacy">
        {t("contact.crmNote")}{" "}
        <Link to="/privacy-policy">{t("contact.privacy")}</Link>.
      </p>

      <div className="vh-cf-actions">
        {submitBanner ? (
          <p className="vh-cf-success" role="status">
            {t("contact.submitReceived")}
          </p>
        ) : null}
        <button type="submit" className="vh-cf-submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <span className="vh-cf-spinner" aria-hidden="true" />
          ) : null}
          <span>{isSubmitting ? t("contact.submitting") : t("contact.submit")}</span>
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
